<template>
    <client-only>
        <!--
             @vue2-leaflet imports entire leaflet library thus increasing our vendor bundle size drastically.
             Having leaflet dynamically imported solved this issue. The library is now loaded on demand only.
             CDN version also has a high chance of being cached on a client side
         -->
        <div class="leaflet-map" ref="mapElement">
            <div class="map-popup" v-if="activeMarker" ref="popup">
                <slot name="popup" :entry="activeMarkerEntry">
                    <GridItem :block="activeMarkerEntry" class="map-popup-block"></GridItem>
                </slot>
            </div>
        </div>
    </client-only>
</template>

<script>
import GridItem from '@/components/grid/GridItem';
import priceModes from '@/models/priceModes';
import { appendScript, appendStyle } from '@/utils/injectRemote';

export default {
    name: 'LeafletMap',
    data() {
        return {
            map: null,
            markersLayer: null,
            mapOptions: {
                //zoom: 7,
                //center: [50.906861 , 6.976514],
                zoomControl: true,
                attributionControl: true,
                zoomSnap: true
            },
            tileUrl: `${this.$config.tileHost}/{z}/{x}/{y}.png`,
            tileOptions: {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            },
            iconOptions: {
                iconUrl: require('@/assets/icons/map-marker.png'),
                iconRetinaUrl: require('@/assets/icons/map-marker_2x.png'),
                iconSize: [33, 45],
                iconAnchor: [16, 45],
                popupAnchor: [0, -22]
            },
            popupOptions: {
                maxWidth: 360
            },
            activeMarker: null
        };
    },
    components: {
        GridItem
    },
    props: {
        markers: {
            type: Array,
            required: true
        },
        activateSingle: Boolean
    },
    computed: {
        markersWithKeys() {
            return this.markers.map((marker, index) => ({
                ...marker,
                key: Math.random(100).toString(16).substring(2) //key will be used to compare marker objects
            }));
        },
        priceModeProperty() {
            return this.$store.state.priceMode.property;
        },
        activeMarkerEntry() {
            //Product details, hotel details, voucher details marker
            if (this.activeMarker.entry) {
                return this.activeMarker.entry;
            }

            //home markers, provided in the correct form
            if (this.priceModeProperty === priceModes.package.property) {
                return this.activeMarker.cheapestPackagePriceEntry;
            } else if (this.priceModeProperty === priceModes.perPerson.property) {
                return this.activeMarker.cheapestPerPersonPriceEntry;
            } else {
                return this.activeMarker.cheapestPerPersonPerNightPriceEntry;
            }
        }
    },
    methods: {
        preventFromMovingSwiper() {
            const swiperNoMoveClass = 'swiper-no-swiping';
            this.map.on('mousedown', () => {
                this.$refs.mapElement.classList.add(swiperNoMoveClass);
            });

            this.map.on('mouseup', () => {
                this.$refs.mapElement.classList.remove(swiperNoMoveClass);
            });
        },
        initialize() {
            this.map = L.map(this.$refs.mapElement, this.mapOptions);

            this.preventFromMovingSwiper();

            L.tileLayer(this.tileUrl, this.tileOptions).addTo(this.map);

            this.markersLayer = new L.FeatureGroup();

            this.markersLayer.addTo(this.map);

            this.updateMarkersLayer();
        },
        updateMarkersLayer() {
            if (!this.markersLayer) {
                this.markersLayer = new L.FeatureGroup();
                this.markersLayer.addTo(this.map);
            }

            this.markersLayer.clearLayers();

            const icon = L.icon(this.iconOptions);

            this.markersWithKeys.forEach(marker => {
                const domMarker = L.marker(marker.pos, {
                    icon,
					alt: this.$getSafe(marker, 'entry.hotelName') || 'Hotelmarkierungssymbol',
					title: this.$getSafe(marker, 'entry.hotelName') || 'Hotelmarkierungssymbol'
                });
                domMarker
                    .on('click', () => {
                        this.activeMarker = marker;
                        this.$nextTick(() => {
                            domMarker.bindPopup(this.$refs.popup, this.popupOptions).openPopup();
                        });
                    })
                    .addTo(this.markersLayer);
            });

            if (this.markersLayer.getLayers().length > 0) {
                this.map.fitBounds(this.markersLayer.getBounds());
            }
        }
    },
    watch: {
        markersWithKeys() {
            this.map && this.updateMarkersLayer();
        }
    },
    async mounted() {
        try {
            await appendStyle('https://unpkg.com/leaflet@1.6.0/dist/leaflet.css', {
                integrity:
                    'sha512-xwE/Az9zrjBIphAcBb3F6JVqxf46+CDLwfLMHloNu6KEQCAWi6HcDUbeOfBIptF7tcCzusKFjFw2yuvEpDL9wQ==',
                crossOrigin: ''
            });

            await appendScript('https://unpkg.com/leaflet@1.6.0/dist/leaflet.js', {
                integrity:
                    'sha512-gZwIG9x3wUXg2hdXF6+rVkLF/0Vi9U8D2Ntg4Ga5I5BZpVkVxlJWbSQtXPSiUTtC0TjtGOmxa1AJPuV0CPthew==',
                crossOrigin: ''
            });

            //
            this.$intersectionObserver.add(this.$refs.mapElement, this.initialize);
        } catch (e) {
            this.$isDev && console.error(e);
        }
    },
    beforeDestroy() {
        this.map && this.map.remove();
        this.$intersectionObserver.unobserve(this.$refs.mapElement);
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/bootstrap/breakpoints' as breakpoints;
@use 'sass:map';

.leaflet-map {
    height: 550px;
    width: 100%;
    z-index: 1; //prevents map from overlapping mobile header while scrolling

    .map-popup {
        font-family: var(--font-family-sans-serif);

        .map-popup-block {
            @include breakpoints.media-breakpoint-up(lg) {
                width: map-get(variables.$grid, 'tile-width-lg');
            }

            @include breakpoints.media-breakpoint-down(variables.$mobile-breakpoint-max) {
                width: map-get(variables.$grid, 'tile-width-mobile');
                max-width: 80vw;
            }
        }
    }

	::v-deep .leaflet-control-attribution.leaflet-control a {
		color: var(--primary-accessible, var(--primary))
	}
}
</style>
